import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PopupBaseComponent } from '../../../../../shared/components/popup/popup-base/popup-base.component';
import { ButtonComponent, ButtonType } from '../../../../../shared/components/button/button.component';
import { OptionToggleComponent } from '../../../../../shared/components/option-toggle/option-toggle.component';
import { Option } from '../../../../../shared/types/option';
import { LayoutStore, LayoutStoreType } from '../../../../../core/store/layout.store';
import { Toast, ToastType } from '../../../../../shared/types/toast';
import { AccessRequestsService } from '../../../../access-requests/service/access-requests.service';
import {
    CreateAccessRequestContract,
    comparePropertyAdmins,
    getPropertyAdminRoleLabel,
    PropertyAdmin,
} from '../../../../access-requests/types/access-request';
import { RequestableProperty, RequestableSourceState, RequestAccessPopupModel } from './request-access-popup.model';

const NOTE_MAX_LENGTH = 500;
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const JUYO_SUPPORT = 'Juyo support';

interface SourceRow {
    code: string;
    label: string;
    state: RequestableSourceState;
    selected: boolean;
    options: Option[];
    selectedOption: Option | undefined;
}

@Component({
    selector: 'juyo-request-access-popup',
    styleUrl: './request-access-popup.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopupBaseComponent, ButtonComponent, ReactiveFormsModule, OptionToggleComponent],
    template: `
        <juyo-popup-base [popupId]="model().id" [maxWidth]="'500px'">
            <span slot="header">Request access</span>

            <div slot="body" class="body">
                <p class="subtitle">Each source goes to an admin who has it at this property.</p>

                <div class="field">
                    <span class="field-label">Property</span>
                    <juyo-option-toggle
                        [options]="propertyOptions()"
                        [selectedOption]="selectedPropertyOption()"
                        [openOnClick]="true"
                        [useBorder]="true"
                        [useColorSunken]="true"
                        [useFullWidth]="true"
                        [heightInPixels]="44"
                        [panelHeightInPixels]="240"
                        [placeholder]="'Nothing to request'"
                        [displaySearchBar]="propertyOptions().length > 8"
                        [disabled]="isSending()"
                        (optionClick)="onPropertySelect($event)"
                    />
                </div>

                <div class="field">
                    <span class="field-label" id="request-access-sources">Data sources</span>
                    <div class="sources" role="group" aria-labelledby="request-access-sources">
                        @for (row of sourceRows(); track row.code) {
                            <div
                                class="source"
                                [class.source--locked]="row.state === 'locked'"
                                [class.source--selected]="row.selected"
                            >
                                <button
                                    type="button"
                                    class="source-header"
                                    role="checkbox"
                                    [attr.aria-checked]="row.state !== 'locked' || row.selected"
                                    [disabled]="row.state !== 'locked' || isSending()"
                                    (click)="toggleSource(row.code)"
                                >
                                    <span class="checkbox" aria-hidden="true">
                                        @if (row.state !== 'locked' || row.selected) {
                                            <span class="material-symbols-outlined">check</span>
                                        }
                                    </span>
                                    <span class="source-name">{{ row.label }}</span>
                                    @switch (row.state) {
                                        @case ('enabled') {
                                            <span class="badge badge--access">You have access</span>
                                        }
                                        @case ('pending') {
                                            <span class="badge badge--pending">Requested</span>
                                        }
                                    }
                                </button>

                                @if (row.selected) {
                                    <div class="route">
                                        <span class="route-label">Goes to</span>
                                        @if (row.options.length) {
                                            <juyo-option-toggle
                                                [options]="row.options"
                                                [selectedOption]="row.selectedOption"
                                                [openOnClick]="true"
                                                [useBorder]="true"
                                                [useColorSunken]="true"
                                                [useFullWidth]="true"
                                                [heightInPixels]="38"
                                                [panelHeightInPixels]="200"
                                                [disabled]="isSending()"
                                                (optionClick)="onRouteSelect(row.code, $event)"
                                            />
                                        } @else {
                                            <span class="route-support">
                                                Juyo support
                                                <span class="muted">· no admin here has it yet</span>
                                            </span>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>
                    @if (errorMessage()) {
                        <p class="error-text" role="alert">{{ errorMessage() }}</p>
                    }
                </div>

                <div class="field">
                    <label class="field-label" for="request-access-note"
                        >Note <span class="muted">· optional</span></label
                    >
                    <textarea
                        id="request-access-note"
                        class="input textarea"
                        placeholder="I need outlet sales for the F&amp;B budget review"
                        [attr.maxlength]="noteMaxLength"
                        [formControl]="noteControl"
                    ></textarea>
                </div>
            </div>

            <ng-container slot="footer">
                <span class="summary">{{ summary() }}</span>
                <juyo-button [buttonType]="ButtonType.TRANSPARENT" (clicked)="onCancel()">Cancel</juyo-button>
                <juyo-button [buttonType]="ButtonType.PURPLE" [isLoading]="isSending()" (clicked)="onSend()">
                    Send request
                </juyo-button>
            </ng-container>
        </juyo-popup-base>
    `,
})
export class RequestAccessPopupComponent implements OnInit {
    public model = input.required<RequestAccessPopupModel>();

    protected readonly ButtonType = ButtonType;
    protected readonly noteMaxLength = NOTE_MAX_LENGTH;

    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private accessRequestsService = inject(AccessRequestsService);
    private destroyRef = inject(DestroyRef);

    protected noteControl = new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(NOTE_MAX_LENGTH)],
    });
    private routes = signal<Record<string, number>>({});

    private selectedPropertyId = signal<number | null>(null);
    private selectedSources = signal<string[]>([]);
    protected errorMessage = signal('');
    protected isSending = signal(false);

    protected propertyOptions = computed(() =>
        this.model()
            .properties.filter(hasLockedSource)
            .map((property) => new Option(String(property.id), property.name)),
    );

    protected selectedPropertyOption = computed(() => {
        const id = this.selectedPropertyId();
        return this.propertyOptions().find((option) => option.key === String(id));
    });

    protected property = computed(() => {
        const id = this.selectedPropertyId();
        return this.model().properties.find((property) => property.id === id) ?? null;
    });

    private selectedSet = computed(() => new Set(this.selectedSources()));

    protected sourceRows = computed<SourceRow[]>(() => {
        const property = this.property();
        if (!property) {
            return [];
        }

        const routes = this.routes();
        const selected = this.selectedSet();
        return property.sources.map(({ code, state }) => {
            const options =
                state === 'locked'
                    ? getGrantors(property, code).map(
                          (admin) =>
                              new Option(String(admin.userId), `${admin.name} · ${getPropertyAdminRoleLabel(admin.role)}`),
                      )
                    : [];
            return {
                code,
                label: formatSourceLabel(code),
                state,
                selected: selected.has(code),
                options,
                selectedOption: options.find((option) => option.key === String(routes[code])),
            };
        });
    });

    protected summary = computed(() => {
        const sources = this.selectedSources();
        if (!sources.length) {
            return '';
        }
        const routes = this.routes();
        const approvers = new Set(sources.map((code) => routes[code] ?? JUYO_SUPPORT));
        return `${pluralize(sources.length, 'source')} · ${pluralize(approvers.size, 'approver')}`;
    });

    public ngOnInit(): void {
        const { properties, initialPropertyId, initialSource } = this.model();
        const requestable = properties.filter(hasLockedSource);
        const initial =
            requestable.find((property) => property.id === initialPropertyId) ?? requestable[0] ?? null;

        this.selectedPropertyId.set(initial?.id ?? null);

        if (
            initial &&
            initialSource &&
            initial.sources.some((source) => source.code === initialSource && source.state === 'locked')
        ) {
            this.toggleSource(initialSource);
        }
    }

    protected onPropertySelect(option: Option): void {
        const id = Number(option.key);
        if (id === this.selectedPropertyId()) {
            return;
        }
        this.clearSelection();
        this.errorMessage.set('');
        this.selectedPropertyId.set(id);
    }

    protected onRouteSelect(code: string, option: Option): void {
        this.routes.update((routes) => ({ ...routes, [code]: Number(option.key) }));
    }

    protected isSelected(code: string): boolean {
        return this.selectedSet().has(code);
    }

    protected toggleSource(code: string): void {
        const property = this.property();
        if (!property || this.isSending()) {
            return;
        }

        this.errorMessage.set('');

        if (this.isSelected(code)) {
            this.routes.update((routes) =>
                Object.fromEntries(Object.entries(routes).filter(([key]) => key !== code)),
            );
            this.selectedSources.update((sources) => sources.filter((source) => source !== code));
            return;
        }

        const grantors = getGrantors(property, code);
        if (grantors.length) {
            this.routes.update((routes) => ({ ...routes, [code]: grantors[0].userId }));
        }
        this.selectedSources.update((sources) => [...sources, code]);
    }

    protected onSend(): void {
        const property = this.property();
        if (!property || this.isSending()) {
            return;
        }

        if (!hasLockedSource(property)) {
            this.errorMessage.set('You already have everything this property has.');
            return;
        }

        const sources = this.selectedSources();
        if (!sources.length) {
            this.errorMessage.set('Pick at least one data source.');
            return;
        }

        const note = this.noteControl.value.trim().slice(0, NOTE_MAX_LENGTH);
        const contract: CreateAccessRequestContract = {
            hotelId: property.id,
            hotelName: property.name,
            ...(note ? { note } : {}),
            items: sources.map((code) => {
                const adminId = this.routes()[code];
                const admin = getGrantors(property, code).find((grantor) => grantor.userId === adminId);

                return admin
                    ? {
                          dataSource: code,
                          admin: { userId: admin.userId, name: admin.name, email: admin.email, role: admin.role },
                      }
                    : { dataSource: code };
            }),
        };

        this.errorMessage.set('');
        this.isSending.set(true);
        this.accessRequestsService
            .create(this.model().organizationId, contract)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (created) => {
                    this.isSending.set(false);
                    this.model().onSent(created);
                    this.layoutStore.closePopup(this.model().id);
                    this.layoutStore.addTimedToast(
                        new Toast('access-request-sent', [buildSentMessage(contract)], ToastType.Success),
                        3000,
                    );
                },
                error: (error: unknown) => {
                    this.isSending.set(false);
                    this.errorMessage.set(extractErrorMessage(error));
                },
            });
    }

    protected onCancel(): void {
        this.layoutStore.closePopup(this.model().id);
    }

    private clearSelection(): void {
        this.routes.set({});
        this.selectedSources.set([]);
    }
}

function pluralize(count: number, word: string): string {
    return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function formatSourceLabel(code: string): string {
    if (code.length <= 3 || code !== code.toUpperCase()) {
        return code;
    }
    return code.charAt(0) + code.slice(1).toLowerCase();
}

function hasLockedSource(property: RequestableProperty): boolean {
    return property.sources.some((source) => source.state === 'locked');
}

function getGrantors(property: RequestableProperty, code: string): PropertyAdmin[] {
    return property.admins
        .filter((admin) => admin.dataSources.includes(code))
        .sort(comparePropertyAdmins);
}

function buildSentMessage(contract: CreateAccessRequestContract): string {
    const count = contract.items.length;
    const names = new Set<string>();
    let hasSupport = false;

    for (const item of contract.items) {
        if (item.admin) {
            names.add(item.admin.name);
        } else {
            hasSupport = true;
        }
    }
    if (hasSupport) {
        names.add(JUYO_SUPPORT);
    }

    return `${count} request${count === 1 ? '' : 's'} sent to ${[...names].join(' and ')}`;
}

function extractErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const message = (error as { message: unknown }).message;
        if (typeof message === 'string' && message.trim()) {
            return message;
        }
    }
    return GENERIC_ERROR;
}
